import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function MoodDiary() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    mood: 5, sleep_hours: "", medication_taken: null, anxiety: 5, voices: false, self_harm_urge: 0, note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/mood").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/mood", {
        ...form,
        sleep_hours: form.sleep_hours === "" ? null : Number(form.sleep_hours),
      });
      toast.success("Mood entry saved");
      await load();
    } catch {
      toast.error("Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  const chartData = [...entries].reverse().slice(-30).map((e) => ({
    date: new Date(e.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    mood: e.mood,
    anxiety: e.anxiety ?? null,
    urge: e.self_harm_urge ?? null,
  }));

  return (
    <div className="space-y-6 fade-up" data-testid="mood-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Mood diary</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Track patterns so you and your team can spot what helps and what doesn't.</p>
      </header>

      {chartData.length > 1 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="mood-chart">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Last 30 entries</h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 4, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Line type="monotone" dataKey="mood" stroke="#0f766e" strokeWidth={2} dot={false} name="Mood" />
                <Line type="monotone" dataKey="anxiety" stroke="#d97706" strokeWidth={2} dot={false} name="Anxiety" />
                <Line type="monotone" dataKey="urge" stroke="#dc2626" strokeWidth={2} dot={false} name="Self-harm urge" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="mood-form">
        <div>
          <label className="text-sm font-medium text-slate-700">Mood: {form.mood}/10</label>
          <input type="range" min="0" max="10" value={form.mood} onChange={(e) => setForm({ ...form, mood: Number(e.target.value) })} className="w-full accent-teal-700" data-testid="mood-mood" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Anxiety: {form.anxiety}/10</label>
          <input type="range" min="0" max="10" value={form.anxiety} onChange={(e) => setForm({ ...form, anxiety: Number(e.target.value) })} className="w-full accent-amber-600" data-testid="mood-anxiety" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Self-harm urge: {form.self_harm_urge}/10</label>
          <input type="range" min="0" max="10" value={form.self_harm_urge} onChange={(e) => setForm({ ...form, self_harm_urge: Number(e.target.value) })} className="w-full accent-red-600" data-testid="mood-urge" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Sleep (hrs)</label>
            <input type="number" min="0" max="24" step="0.5" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500" data-testid="mood-sleep" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Medication taken</label>
            <select value={form.medication_taken === null ? "" : String(form.medication_taken)} onChange={(e) => setForm({ ...form, medication_taken: e.target.value === "" ? null : e.target.value === "true" })} className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500" data-testid="mood-meds">
              <option value="">—</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Hearing voices</label>
            <select value={String(form.voices)} onChange={(e) => setForm({ ...form, voices: e.target.value === "true" })} className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500" data-testid="mood-voices">
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Note (optional)</label>
          <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500" data-testid="mood-note" />
        </div>
        <button data-testid="mood-submit" disabled={saving} onClick={submit} className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-5 py-2.5 font-semibold">
          {saving ? "Saving…" : "Save mood entry"}
        </button>
      </div>

      {entries.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="mood-history">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Recent entries</h2>
          <div className="divide-y divide-slate-100">
            {entries.slice(0, 10).map((e) => (
              <div key={e.id} className="py-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-800">Mood {e.mood}/10 · Anxiety {e.anxiety}/10</span>
                  <span className="text-slate-400 text-xs">{new Date(e.created_at).toLocaleString("en-GB")}</span>
                </div>
                {e.note && <div className="text-slate-600 mt-0.5">{e.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
